import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

import type { DimensionField, DimensionValues } from '../types/panel-fields'
import { multiSelectionPanelWidthHeight } from './selection-bounds'

/** Canvas points for reverting W/H after blank, invalid, or negative panel input. */
export function panelDimensionRevertPoints(params: {
  field: 'width' | 'height'
  isMultiSelect: boolean
  activeObjects: ShapeData[]
  activeObject: ShapeData | null
  mgr: ShapeManagerRef | null | undefined
}): number {
  const { field, isMultiSelect, activeObjects, activeObject, mgr } = params

  if (isMultiSelect) {
    if (activeObjects.length === 0) return 0
    const panelWh = multiSelectionPanelWidthHeight({
      shapes: activeObjects,
      shapeManager: mgr ?? null,
    })

    return field === 'width' ? panelWh.width : panelWh.height
  }

  if (activeObject) {
    return field === 'width' ? activeObject.width : activeObject.height
  }

  return 0
}

/** W/H field text after showing `formatted`: both fields while linked, otherwise just `field`. */
export function nextDimensionsDisplay(
  prev: DimensionValues,
  field: DimensionField,
  formatted: string,
  linked: boolean
): DimensionValues {
  return linked ? { width: formatted, height: formatted } : { ...prev, [field]: formatted }
}

/** Geometry used when `computeShapeResizeFromPanelDimension` cannot resize (zero or invalid size). */
export function fallbackPanelResizeGeometry(params: {
  shape: ShapeData
  field: DimensionField
  points: number
  linked: boolean
}): { x: number; y: number; width: number; height: number } {
  const { shape, field, points, linked } = params

  if (linked) {
    return { x: shape.x, y: shape.y, width: points, height: points }
  }

  return {
    x: shape.x,
    y: shape.y,
    width: field === 'width' ? points : shape.width,
    height: field === 'height' ? points : shape.height,
  }
}
